import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft, Printer, Download, ShieldCheck, MapPin, CalendarDays, User,
  Phone, Mail, Hash, Package, FileText, Lock, ClipboardList, Building2, DollarSign, Info, CheckCircle2,
  MessageSquare, RefreshCw, Clock, FileUp, Truck, PencilLine,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DispatchDocumentsPanel } from "@/components/lr-direct/DispatchDocumentsPanel";
import { DispatchSheet } from "@/components/lr-direct/DispatchSheet";
import { CreditAppFillPanel } from "@/components/lr-direct/CreditAppFillPanel";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { DispatchTimeline } from "@/components/lr-direct/DispatchTimeline";
import { LoadStageTracker } from "@/components/lr-direct/LoadStageTracker";
import { StagePopover } from "@/components/lr-direct/StagePopover";
import {
  editStageTimeDynamic,
  type DispatchStatus,
} from "@/hooks/useUpdateDispatchStatus";
import { nextActionableStage, actionForStage } from "@/lib/lrDirect/milestoneActions";
import { undoMilestoneCascade } from "@/lib/lrDirect/undoCascade";
import { markStageDone as canonicalMarkStageDone, closeLoadManual, reopenLoad } from "@/lib/lrDirect/canonicalMilestones";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NextUpActionBar } from "@/components/lr-direct/dispatch/NextUpActionBar";
import { deriveStages, type DerivedStage } from "@/lib/lrDirect/deriveStages";
import { bucketForRow } from "@/lib/lrDirect/brokerLoadBucket";
import { brokerRowStatus } from "@/lib/lrDirect/brokerRowStatus";
import { type DispatchStop, type LoadStop } from "@/hooks/useDirectDispatch";
import { useHasBrokerAccepted, acceptDispatch } from "@/hooks/useDispatchAcceptance";
import { useDispatchDocuments } from "@/hooks/useDispatchDocuments";
import { useLoadDetail } from "@/hooks/useLoadDetail";
import { useChatSystem } from "@/hooks/useChatSystem";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";
import { supabase } from "@/integrations/supabase/client";
import { DispatchETACards } from "@/components/lr-direct/DispatchETACards";
import { DispatchTrackingLinkCard } from "@/components/lr-direct/DispatchTrackingLinkCard";
import { AddLoadNoteCard } from "@/components/lr-direct/AddLoadNoteCard";
import { LoadNotesHistoryPanel } from "@/components/lr-direct/LoadNotesHistoryPanel";
import { useLoadNotes } from "@/hooks/useLoadNotes";

import { displayCompanyName } from "@/lib/displayCompanyName";



interface BrokerDispatchViewProps {
  load: any;
  dispatchId: string | undefined;
  status: DispatchStatus | string;
  stops: DispatchStop[];
  loadStops?: LoadStop[];
  loadPallets: number | null;
  loadWeight: number | null;
  loadCommodity: string | null;
  loadNotes: string | null;
  awardedAmount: number | null;
  totalMiles: number | null;
  awardedAt: string | null;
  loadId: string;
  actorId: string;
  shipperUserId: string | null;
  /** Set only after the shipper has actually saved the dispatch builder. */
  dispatchPreparedAt: string | null;
  onRefresh: () => void;
}

interface ShipperInfo {
  name: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
}

interface MergedStop extends DispatchStop {
  pickup_number?: string | null;
}

function formatDate(ymd?: string | null): string {
  if (!ymd) return "—";
  const d = parseYYYYMMDDToLocalDate(ymd);
  return d ? format(d, "EEE, MMM d, yyyy") : ymd;
}

function formatTimeWindow(start?: string | null, end?: string | null): string | null {
  if (!start && !end) return null;
  if (start && end) return `${start} – ${end}`;
  return start ?? end ?? null;
}

/** Relative age for the "shipper updated this dispatch" chip. */
function formatEditAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Prefer dispatch value when present and not blank, otherwise fall back to load value. */
function pick<T>(a: T | null | undefined, b: T | null | undefined): T | null {
  if (a !== null && a !== undefined && a !== "") return a;
  if (b !== null && b !== undefined && b !== "") return b;
  return null;
}


export function BrokerDispatchView({
  load, dispatchId, status, stops, loadStops, loadPallets, loadWeight, loadCommodity, loadNotes,
  awardedAmount, totalMiles, awardedAt, loadId, actorId, shipperUserId, dispatchPreparedAt, onRefresh,
}: BrokerDispatchViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openChatWithUser } = useChatSystem();
  const [shipper, setShipper] = useState<ShipperInfo | null>(null);
  const [shipperLoaded, setShipperLoaded] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const { accepted, refresh: refreshAccepted } = useHasBrokerAccepted(dispatchId);
  const { docs } = useDispatchDocuments(dispatchId);
  const { notes } = useLoadNotes(loadId);
  const hasPod = useMemo(() => docs.some((d) => d.doc_type === "pod"), [docs]);

  const statusStr = String(status ?? "");
  // "Preparing" = the dispatch row hasn't been created yet, OR the shipper
  // has never opened the builder and saved. We can't rely on stop counts —
  // sync_dispatch_stops_from_load auto-mirrors load stops onto the dispatch
  // on award, so a fresh, untouched dispatch always has stops. Only the
  // explicit `dispatch_prepared_at` stamp (set on the shipper's first save)
  // is a reliable signal that a real dispatch packet exists.
  // Belt-and-braces: a row still sitting in `draft` is work-in-progress even if
  // a legacy stray `dispatch_prepared_at` stamp exists. Ready dispatches carry
  // ready/assigned/in_transit/picked_up/delivered/closed — never `draft`.
  const isPreparation = !dispatchId || !dispatchPreparedAt || statusStr === "draft";

  const isDelivered = statusStr === "delivered";
  const isClosed = statusStr === "closed";

  // Detailed load data for the interactive stage tracker (stages, events, POD).
  const { data: detail, refetch: refetchDetail } = useLoadDetail(loadId);
  /** A CARRIER shares this view with the awarded broker — only fall back to the
   *  awarded broker's company name when the viewer actually IS that broker. */
  const viewerIsAwardedBroker =
    !!actorId && (detail as any)?.awardedBroker?.user_id === actorId;
  const stages = useMemo<DerivedStage[]>(() => {
    if (!detail?.load) return [];
    return deriveStages({
      load: detail.load,
      dispatch: detail.dispatch,
      stops: detail.dispatchStops as any[],
      events: detail.events,
      podDoc: detail.documents.find((d: any) => d.doc_type === "pod") ?? null,
    });
  }, [detail]);
  const refreshAll = () => { onRefresh(); refetchDetail(); };

  // Most recent shipper edit AFTER the packet was ready — drives the amber
  // "shipper updated this dispatch" awareness chip. Realtime already refreshes
  // the events list, so this stays current without extra plumbing.
  const lastEdit = useMemo(() => {
    const list = (detail?.events ?? []) as Array<{ event_type: string; created_at: string; payload: any }>;
    const edits = list.filter((e) => e.event_type === "dispatch_edited");
    if (edits.length === 0) return null;
    return edits.reduce((a, b) => (new Date(b.created_at) > new Date(a.created_at) ? b : a));
  }, [detail?.events]);


  // Optimistic tracker fill: patch react-query cache (events + stop rows) so
  // the circle lights up instantly on click, before the network round-trip.
  const cacheKey = ["lr-direct-load-detail", loadId] as const;
  const optimisticallyMarkStage = (stage: DerivedStage) => {
    const prev = queryClient.getQueryData<any>(cacheKey);
    if (!prev) return;
    const nowIso = new Date().toISOString();
    if (stage.kind === "broker_stop" && stage.stopId && stage.stopType && stage.stopNumber && stage.subKind) {
      const eventType =
        stage.subKind === "checked_in"
          ? `${stage.stopType}_${stage.stopNumber}_checked_in`
          : stage.stopType === "pickup"
            ? `pickup_${stage.stopNumber}_loaded`
            : `delivery_${stage.stopNumber}_delivered`;
      const nextStopStatus = stage.subKind === "done" ? "done" : "checked_in";
      const nextStops = (prev.dispatchStops ?? []).map((s: any) =>
        s.id === stage.stopId
          ? {
              ...s,
              status: nextStopStatus,
              checked_in_at: s.checked_in_at ?? nowIso,
              completed_at: stage.subKind === "done" ? nowIso : s.completed_at,
            }
          : s,
      );
      queryClient.setQueryData(cacheKey, {
        ...prev,
        dispatchStops: nextStops,
        events: [
          { id: `optimistic-${eventType}-${nowIso}`, dispatch_id: prev.dispatch?.id, actor_id: actorId, event_type: eventType, payload: {}, created_at: nowIso, actor_name: "You" },
          ...(prev.events ?? []),
        ],
      });
    } else if (stage.kind === "broker_dispatch") {
      const eventType = `status_${stage.key}`;
      queryClient.setQueryData(cacheKey, {
        ...prev,
        events: [
          { id: `optimistic-${eventType}-${nowIso}`, dispatch_id: prev.dispatch?.id, actor_id: actorId, event_type: eventType, payload: {}, created_at: nowIso, actor_name: "You" },
          ...(prev.events ?? []),
        ],
      });
    }
  };

  const optimisticallyUndoStage = (stage: DerivedStage) => {
    const prev = queryClient.getQueryData<any>(cacheKey);
    if (!prev) return;
    const toClear = new Set<string>();
    // Clear this stage's event(s) + all later broker events.
    const idx = stages.findIndex((s) => s.key === stage.key);
    for (let i = idx; i < stages.length; i++) {
      const s = stages[i];
      if (s.kind === "broker_dispatch") toClear.add(`status_${s.key}`);
      if (s.kind === "broker_stop" && s.stopType && s.stopNumber && s.subKind) {
        const et = s.subKind === "checked_in"
          ? `${s.stopType}_${s.stopNumber}_checked_in`
          : s.stopType === "pickup" ? `pickup_${s.stopNumber}_loaded` : `delivery_${s.stopNumber}_delivered`;
        toClear.add(et);
      }
    }
    const nextEvents = (prev.events ?? []).filter((e: any) => !toClear.has(e.event_type));
    // Revert stop row if it's a broker_stop.
    const nextStops = (prev.dispatchStops ?? []).map((s: any) => {
      if (stage.kind === "broker_stop" && s.id === stage.stopId) {
        if (stage.subKind === "checked_in") {
          return { ...s, status: "pending", checked_in_at: null, completed_at: null };
        }
        return { ...s, status: "checked_in", completed_at: null };
      }
      return s;
    });
    queryClient.setQueryData(cacheKey, { ...prev, events: nextEvents, dispatchStops: nextStops });
  };




  // Plain-English status badge (reuse My Loads mapping).
  const bucket = useMemo(
    () => bucketForRow({
      bid_status: "accepted",
      is_awarded: true,
      lost_to_other: false,
      dispatch_status: statusStr || null,
      dispatch_archived_at: null,
    }),
    [statusStr],
  );
  const rowStatus = useMemo(() => brokerRowStatus({
    bucket,
    bid_status: "accepted",
    is_awarded: true,
    dispatch_status: statusStr || null,
    dispatch_archived_at: null,
  }), [bucket, statusStr]);

  const onAcceptDispatch = async () => {
    if (!dispatchId || accepting) return;
    setAccepting(true);
    try {
      const { alreadyAccepted } = await acceptDispatch({
        dispatchId,
        loadId,
        brokerId: actorId,
        shipperUserId,
        loadNumber: load?.load_number ?? null,
        currentStatus: statusStr,
      });
      toast.success(alreadyAccepted ? "Dispatch already accepted" : "Dispatch accepted");
      await refreshAccepted();
      refreshAll();
    } catch (e: any) {
      toast.error(e?.message || "Failed to accept dispatch");
    } finally {
      setAccepting(false);
    }
  };

  /** Shared milestone writer — used by BOTH the NEXT UP button and the tracker
   *  popover so there is exactly one way to complete a milestone. */
  const markStageDone = async (s: DerivedStage) => {
    if (!dispatchId) return;
    optimisticallyMarkStage(s);
    try {
      await canonicalMarkStageDone({
        stage: s, dispatchId, loadId, actorId,
        counterpartyUserId: shipperUserId, shipperUserId,
        loadNumber: load?.load_number ?? null,
      });
    } catch (e) {
      refetchDetail();
      throw e;
    }
    refreshAll();
  };

  /** Undo a milestone AND every later completed milestone, then re-sync the
   *  legacy dispatch.status column. */
  const undoStageCascade = async (s: DerivedStage) => {
    if (!dispatchId) return;
    optimisticallyUndoStage(s);
    try {
      await undoMilestoneCascade({
        stages, target: s, dispatchId, loadId, actorId, accepted: accepted === true,
      });
    } catch (e) {
      refetchDetail();
      throw e;
    }
    refreshAll();
  };

  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [closing, setClosing] = useState(false);

  /** Manual close — allowed after Delivered even with no POD on file. */
  const doCloseLoad = async () => {
    if (!dispatchId) return;
    setClosing(true);
    try {
      await closeLoadManual({
        dispatchId, loadId, actorId,
        counterpartyUserId: shipperUserId,
        hasPod,
      });
      toast.success("Load closed");
      setConfirmClose(false);
      refreshAll();
    } catch (e: any) {
      toast.error(e?.message || "Failed to close load");
    } finally {
      setClosing(false);
    }
  };

  /** Reopen — clears closed state only; documents, notes and history survive. */
  const doReopenLoad = async () => {
    if (!dispatchId) return;
    setClosing(true);
    try {
      await reopenLoad({
        dispatchId, loadId, actorId, stages,
        accepted: accepted === true,
        counterpartyUserId: shipperUserId,
      });
      toast.success("Load reopened — corrections are available again");
      setConfirmReopen(false);
      refreshAll();
    } catch (e: any) {
      toast.error(e?.message || "Failed to reopen load");
    } finally {
      setClosing(false);
    }
  };

  const scrollToDocs = () => {
    const el = document.getElementById("dispatch-documents");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const messageShipper = () => {
    if (!shipperUserId) {
      toast.error("Shipper contact not available");
      return;
    }
    openChatWithUser(shipperUserId, shipper?.name || "Shipper", undefined, loadId);
  };

  // ONE next action, derived from the canonical milestone list.
  const nextStage = useMemo(() => nextActionableStage(stages), [stages]);

  const iconForStage = (s: DerivedStage) => {
    if (s.kind === "broker_dispatch") return Truck;
    if (s.subKind === "checked_in") return MapPin;
    return s.stopType === "pickup" ? Package : CheckCircle2;
  };

  const renderNextUp = () => {
    if (!dispatchId) return null;

    if (isClosed) {
      return (
        <NextUpActionBar
          label="Reopen Load"
          hint="This load is closed. Reopen it to correct milestones — documents and notes are kept."
          icon={RefreshCw}
          busy={closing}
          onClick={() => setConfirmReopen(true)}
        />
      );
    }

    if (accepted !== true) {
      if (statusStr === "ready" || statusStr === "assigned") {
        return (
          <NextUpActionBar
            label="Accept Dispatch"
            hint="Confirm you've received this dispatch packet before updating milestones."
            icon={CheckCircle2}
            busy={accepting}
            onClick={onAcceptDispatch}
          />
        );
      }
      return null;
    }

    if (nextStage) {
      const { verb, hint } = actionForStage(nextStage);
      return (
        <NextUpActionBar
          label={verb}
          hint={hint}
          icon={iconForStage(nextStage)}
          busy={advancing}
          onClick={async () => {
            setAdvancing(true);
            try {
              await markStageDone(nextStage);
              toast.success(`${nextStage.label} marked done`);
            } catch (e: any) {
              toast.error(e?.message || "Failed to update milestone");
            } finally {
              setAdvancing(false);
            }
          }}
        />
      );
    }

    if (!hasPod) {
      return (
        <div className="space-y-2">
          <NextUpActionBar
            label="Upload POD"
            hint="Upload the signed POD to close out this load."
            icon={FileUp}
            onClick={scrollToDocs}
          />
          <NextUpActionBar
            label="Close Load"
            hint="Close this load without a POD on file. POD stays its own milestone."
            icon={CheckCircle2}
            busy={closing}
            onClick={() => setConfirmClose(true)}
          />
        </div>
      );
    }
    return (
      <NextUpActionBar
        label="Close Load"
        hint="All milestones are complete — close this load out."
        icon={CheckCircle2}
        busy={closing}
        onClick={() => setConfirmClose(true)}
      />
    );
  };


  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Use the winner-gated security-definer RPC. Direct `profiles` SELECTs
      // are blocked for the broker under RLS (email/phone are locked), so
      // this is the only path that returns the shipper's contact.
      const { data, error } = await supabase.rpc(
        "get_direct_award_contact" as any,
        { p_load_id: loadId } as any,
      );
      if (cancelled) return;
      if (error) {
        console.error("[BrokerDispatchView] get_direct_award_contact failed", error);
        setShipperLoaded(true);
        return;
      }
      const row = (data as any[])?.[0];
      if (row) {
        setShipper({
          name: displayCompanyName(row.company_name) || row.contact_name || "Shipper",
          contactName: row.contact_name ?? null,
          email: row.email ?? null,
          phone: row.phone ?? null,
        });
      }
      setShipperLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [loadId]);


  const rpm = awardedAmount && totalMiles ? (awardedAmount / totalMiles) : null;

  // Merge dispatch stops with load stops by (stop_number, stop_type).
  // Dispatch values win when present; otherwise fall back to load values.
  // If dispatch has no stops at all, fall back entirely to load stops.
  const mergedStops: MergedStop[] = useMemo(() => {
    const ls = loadStops ?? [];
    const byKey = new Map<string, LoadStop>();
    ls.forEach((s) => byKey.set(`${s.stop_type}-${s.stop_number}`, s));

    const source = stops.length > 0 ? stops : ls.map((s) => ({
      stop_number: s.stop_number,
      stop_type: s.stop_type,
    } as DispatchStop));

    return source.map((d) => {
      const l = byKey.get(`${d.stop_type}-${d.stop_number}`);
      return {
        ...d,
        name: pick(d.name, l?.name),
        address: pick(d.address, l?.address),
        city: pick(d.city, l?.city),
        state: pick(d.state, l?.state),
        postal_code: pick(d.postal_code, l?.postal_code),
        scheduled_date: pick(d.scheduled_date, l?.scheduled_date),
        scheduled_time_start: pick(d.scheduled_time_start, l?.scheduled_time_start),
        scheduled_time_end: pick(d.scheduled_time_end, l?.scheduled_time_end),
        contact_name: pick(d.contact_name, l?.contact_name),
        contact_phone: pick(d.contact_phone, l?.contact_phone),
        contact_email: pick(d.contact_email, l?.contact_email),
        instructions: pick(d.instructions, l?.instructions),
        reference_number: pick(d.reference_number, (l as any)?.reference_number ?? l?.reference_numbers),
        // The dispatch row's own pickup/reference number must win here — dropping it
        // was hiding the yellow "Confidential Dispatch Info" block on this page.
        pickup_number: pick<any>(
          (d as any).pickup_number ?? d.reference_number,
          (l as any)?.pickup_number ?? (l as any)?.reference_number,
        ),
        pallet_count: d.pallet_count ?? l?.pallet_count ?? null,
        confidential_notes: pick(d.confidential_notes, l?.confidential_notes),
      };
    });
  }, [stops, loadStops]);

  // For the very first pickup/last delivery, fall back to origin_*/destination_* on the load
  // if a stop is still missing core info.
  const stopsForDisplay: MergedStop[] = useMemo(() => {
    return mergedStops.map((s) => {
      if (s.stop_type === "pickup" && s.stop_number === 1) {
        return {
          ...s,
          name: pick(s.name, load.origin_name),
          address: pick(s.address, load.origin_address),
          city: pick(s.city, load.origin_city),
          state: pick(s.state, load.origin_state),
          postal_code: pick(s.postal_code, load.origin_postal_code),
          scheduled_date: pick(s.scheduled_date, load.pickup_date),
        };
      }
      if (s.stop_type === "delivery") {
        return {
          ...s,
          name: pick(s.name, load.destination_name),
          address: pick(s.address, load.destination_address),
          city: pick(s.city, load.destination_city),
          state: pick(s.state, load.destination_state),
          postal_code: pick(s.postal_code, load.destination_postal_code),
          scheduled_date: pick(s.scheduled_date, load.delivery_date),
        };
      }
      return s;
    });
  }, [mergedStops, load]);


  const openPrintSheet = () => window.open(`/lr-direct/dispatch/${loadId}`, "_blank");

  // ============================================================
  // PHASE 4 FIX 1 — "Dispatch in preparation" early-return state
  // Shown when dispatch row doesn't exist yet, or status is still 'draft'.
  // Hides stops/load-details/documents until shipper marks Ready.
  // ============================================================
  if (isPreparation) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="icon" onClick={() => navigate("/lr-direct/my-wins")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="h-10 w-10 rounded-md bg-brand-teal/10 flex items-center justify-center shrink-0">
              <ClipboardList className="h-5 w-5 text-brand-teal" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal mb-0.5">
                Awarded Load
              </div>
              <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">
                Load {load.load_number}
              </h1>
              <p className="text-xs text-muted-foreground truncate">
                {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                {totalMiles ? ` · ${Math.round(totalMiles).toLocaleString()} mi` : ""}
                {awardedAt ? ` · Awarded ${format(new Date(awardedAt), "MMM d, yyyy")}` : ""}
              </p>
            </div>
          </div>
        </div>

        {/* AWARD BANNER */}
        <Card className="p-3 mb-4 border-brand-teal/25 bg-brand-teal/[0.06] shadow-sm">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-brand-teal" />
            <span className="font-medium">You're awarded this load.</span>
          </div>
        </Card>

        {/* PREPARATION CARD */}
        <Card className="p-6 mb-4 text-center border-lr-yellow/30 bg-lr-yellow/[0.07] shadow-sm">
          <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-3">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-base font-semibold text-foreground mb-1">
            Your shipper is preparing the dispatch packet.
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            You'll get a notification the moment it's ready (usually within 24 hours of award).
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button
              size="sm"
              className="gap-1.5 bg-brand-teal hover:bg-brand-teal/90 text-white"
              onClick={messageShipper}
            >
              <MessageSquare className="h-4 w-4" /> Message Shipper
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        </Card>

        {/* LANE + AWARDED + SHIPPER SUMMARY */}
        <div className="grid gap-4 md:grid-cols-2 mb-4">
          <Card className="p-4 border-brand-teal/15 bg-brand-teal/[0.03] shadow-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Lane & Schedule</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
                <div className="font-medium">
                  {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CalendarDays className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Pickup: {formatDate(load.pickup_date)}</div>
                  {load.delivery_date && (
                    <div className="text-muted-foreground">Delivery: {formatDate(load.delivery_date)}</div>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <DollarSign className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">
                    {awardedAmount != null
                      ? `$${awardedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                      : "Awarded — amount pending"}
                  </div>
                  {rpm != null && (
                    <div className="text-xs text-muted-foreground">${rpm.toFixed(2)}/mile</div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 border-brand-teal/15 bg-brand-teal/[0.03] shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-brand-teal" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shipper</h3>
            </div>
            {shipper ? (
              <div className="space-y-1.5 text-sm">
                <div className="font-semibold text-foreground">{shipper.name}</div>
                {shipper.contactName && shipper.contactName !== shipper.name && (
                  <div className="text-muted-foreground">{shipper.contactName}</div>
                )}
                {shipper.phone && (
                  <a href={`tel:${shipper.phone}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-brand-teal">
                    <Phone className="h-3.5 w-3.5" /> {formatPhoneNumber(shipper.phone)}
                  </a>
                )}
                {shipper.email && (
                  <a href={`mailto:${shipper.email}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-brand-teal truncate">
                    <Mail className="h-3.5 w-3.5" /> {shipper.email}
                  </a>
                )}
              </div>
            ) : shipperLoaded ? (
              <div className="text-muted-foreground text-sm">Contact info unavailable. Use Message Shipper to reach them.</div>
            ) : (
              <div className="text-muted-foreground text-sm">Loading shipper info…</div>
            )}

          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      {/* HEADER + top-right actions */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={() => navigate("/lr-direct/my-wins")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-10 w-10 rounded-md bg-brand-teal/10 flex items-center justify-center shrink-0">
            <ClipboardList className="h-5 w-5 text-brand-teal" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal mb-0.5">
              Dispatch Confirmation
            </div>
            <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">
              Load {load.load_number}
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
              {totalMiles ? ` · ${Math.round(totalMiles).toLocaleString()} mi` : ""}
              {awardedAt ? ` · Awarded ${format(new Date(awardedAt), "MMM d, yyyy")}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={messageShipper}>
            <MessageSquare className="h-4 w-4" /> Message
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 hidden sm:inline-flex" onClick={openPrintSheet}>
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 flex-1 sm:flex-none"
            onClick={openPrintSheet}
            title="Choose 'Save as PDF' in the print dialog"
          >
            <Download className="h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      {/* SHIPPER EDITED THE PACKET AFTER READY — amber awareness chip so the
          broker never runs on stale info without noticing. */}
      {lastEdit && (
        <Card className="p-3 mb-4 border-amber-400/40 bg-amber-50 shadow-sm">
          <div className="flex items-start gap-2 text-sm">
            <PencilLine className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-amber-900">
                {lastEdit.payload?.in_transit
                  ? `Shipper updated this dispatch while in transit · ${formatEditAge(lastEdit.created_at)}`
                  : `Shipper updated this dispatch · ${formatEditAge(lastEdit.created_at)}`}
              </div>
              {Array.isArray(lastEdit.payload?.changed_fields) && lastEdit.payload.changed_fields.length > 0 && (
                <div className="text-xs text-amber-800 mt-0.5">
                  Changed: {lastEdit.payload.changed_fields.slice(0, 6).join(", ")}
                  {lastEdit.payload.changed_fields.length > 6
                    ? ` +${lastEdit.payload.changed_fields.length - 6} more`
                    : ""}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* AWARD BANNER — now carries the awarded amount inline. */}
      <Card className="p-3 mb-4 border-border bg-card shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
          <div className="flex items-center gap-2.5">
            <span className="h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
              <ShieldCheck className="h-3.5 w-3.5" />
            </span>
            <span className="font-semibold text-foreground">You're awarded this load.</span>
            <span className="text-muted-foreground">Treat the sheet below as your rate confirmation.</span>
          </div>
          {awardedAmount != null && (
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-foreground">
                ${awardedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
              {rpm != null && (
                <span className="text-xs text-muted-foreground">${rpm.toFixed(2)}/mile</span>
              )}
            </div>
          )}
        </div>
      </Card>



      {/* SUMMARY — load facts beside the shipper contact, matching the
          dispatch-confirmation document layout. */}
      <div className="grid gap-3 lg:grid-cols-2 mb-4 items-stretch">
        <Card className="p-4 border-border bg-card shadow-sm h-full">
          <div className="flex items-center gap-3 h-full">
            <div className="h-10 w-10 rounded-lg bg-brand-teal flex items-center justify-center shrink-0">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground leading-tight truncate">
                Load {load.load_number}
              </p>
              <p className="text-xs text-foreground leading-tight truncate">
                {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
              </p>
              <p className="text-xs text-muted-foreground leading-tight">
                {totalMiles ? `${Math.round(totalMiles).toLocaleString()} mi` : null}
                {totalMiles && awardedAt ? " · " : null}
                {awardedAt
                  ? `Awarded ${(() => {
                      const d = parseYYYYMMDDToLocalDate(awardedAt);
                      return d ? format(d, "MMM d, yyyy") : awardedAt;
                    })()}`
                  : null}
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className={rowStatus.tone}>{rowStatus.label}</Badge>
                {accepted === true && (
                  <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" /> Accepted
                  </span>
                )}
              </div>
              {awardedAmount != null && (
                <div className="text-right">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none">Rate</div>
                  <div className="text-base font-bold text-foreground leading-tight mt-0.5">
                    ${awardedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </div>
                  {rpm != null && (
                    <div className="text-[10px] text-muted-foreground leading-none">${rpm.toFixed(2)}/mile</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-4 border-border bg-card shadow-sm h-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 h-full">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shipper Contact</h3>
              </div>
              {shipper ? (
                <>
                  <p className="text-sm font-semibold text-foreground leading-tight truncate">{shipper.name}</p>
                  {shipper.contactName && shipper.contactName !== shipper.name && (
                    <p className="text-xs text-muted-foreground leading-tight truncate">{shipper.contactName}</p>
                  )}
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight mt-0.5">Company</p>
                </>
              ) : shipperLoaded ? (
                <p className="text-xs text-muted-foreground leading-tight">
                  Contact info unavailable. Use <span className="font-medium">Message Shipper</span> to reach them.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground leading-tight">Loading shipper contact…</p>
              )}
            </div>
            {shipper && (
              <div className="flex flex-col gap-1 text-xs text-foreground min-w-0 sm:text-right">
                {shipper.phone ? (
                  <a href={`tel:${shipper.phone}`} className="flex items-center gap-1.5 sm:justify-end font-medium text-foreground hover:text-brand-teal">
                    <Phone className="h-3.5 w-3.5" /> {formatPhoneNumber(shipper.phone)}
                  </a>
                ) : (
                  <div className="text-muted-foreground">—</div>
                )}
                {shipper.email ? (
                  <a href={`mailto:${shipper.email}`} className="flex items-center gap-1.5 sm:justify-end font-medium text-foreground hover:text-brand-teal truncate">
                    <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{shipper.email}</span>
                  </a>
                ) : (
                  <div className="text-muted-foreground">—</div>
                )}
              </div>
            )}
            <Button
              size="sm"
              className="gap-1.5 bg-brand-teal-deep hover:bg-brand-teal-deep/90 text-white shadow-sm shrink-0"
              onClick={messageShipper}
            >
              <MessageSquare className="h-3.5 w-3.5" /> Message Shipper
            </Button>
          </div>
        </Card>
      </div>



      {/* LOAD PROGRESS — canonical milestones + the single NEXT UP action.
          The legacy status dropdown is gone: the tracker circles and this one
          button are now the only way to move a dispatch forward. */}
      <Card className="p-4 mb-4 border-border bg-card shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-brand-teal" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Load Progress</h3>
            <span className="text-xs text-muted-foreground font-normal normal-case">
              Real-time status of this load
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={rowStatus.tone}>{rowStatus.label}</Badge>
            {accepted === true && (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Accepted
              </span>
            )}
          </div>
        </div>

        {/* Clickable stage tracker — same behavior as Load Detail page. */}
        {stages.length > 0 && (
          <div>
            <LoadStageTracker
              appearance="dispatch"
              stages={stages}
              nextKey={nextStage?.key ?? null}
              renderStageButton={
                dispatchId
                  ? (stage, defaultButton) => {
                      const isBroker = stage.kind === "broker_stop" || stage.kind === "broker_dispatch";
                      if (!isBroker) return null;
                      const clickable = (
                        <button
                          type="button"
                          className={defaultButton.props.className + " cursor-pointer hover:ring-2 hover:ring-brand-teal/40 hover:ring-offset-1 transition-shadow"}
                          aria-label={`${stage.label} — click to update`}
                        >
                          {defaultButton.props.children}
                        </button>
                      );
                      return (
                        <StagePopover
                          stage={stage}
                          allStages={stages}
                          closed={isClosed}
                          onRequestReopen={() => setConfirmReopen(true)}
                          onMarkDone={async (s) => {
                            await markStageDone(s);
                            toast.success(`${s.label} marked done`);
                          }}
                          onUndo={async (s) => {
                            await undoStageCascade(s);
                            toast.success(`${s.label} undone`);
                          }}
                          onEditTime={async (s, iso) => {
                            await editStageTimeDynamic({ dispatchId, stage: s, newAtIso: iso, actorId });
                            toast.success("Time updated");
                            refreshAll();
                          }}
                        >
                          {clickable}
                        </StagePopover>
                      );
                    }
                  : undefined
              }
            />

          </div>
        )}

        {/* NEXT ACTION STRIP — sits directly under the tracker, matching the
            approved mockup. */}
        <div className="mt-4">{renderNextUp()}</div>
      </Card>

      {/* INFO CARD ROW — ETAs, tracking link and notes as equal compact tiles. */}
      {dispatchId && detail?.dispatch && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
          <DispatchETACards
            inline
            dispatchId={dispatchId}
            loadId={loadId}
            pickupEtaAt={(detail.dispatch as any).pickup_eta_at ?? null}
            pickupEtaUpdatedAt={(detail.dispatch as any).pickup_eta_updated_at ?? null}
            deliveryEtaAt={(detail.dispatch as any).delivery_eta_at ?? null}
            deliveryEtaUpdatedAt={(detail.dispatch as any).delivery_eta_updated_at ?? null}
            editable={true}
            actorId={actorId}
            counterpartyUserId={shipperUserId}
            shipperUserId={shipperUserId}
            onChanged={refreshAll}
            cardClassName="border-border bg-card shadow-sm"
          />
          <DispatchTrackingLinkCard
            dispatchId={dispatchId}
            loadId={loadId}
            trackingUrl={(detail.dispatch as any).tracking_url ?? null}
            trackingUrlUpdatedAt={(detail.dispatch as any).tracking_url_updated_at ?? null}
            editable={true}
            actorId={actorId}
            counterpartyUserId={shipperUserId}
            shipperUserId={shipperUserId}
            onChanged={refreshAll}
            cardClassName="border-border bg-card shadow-sm !mb-0"
          />
          <AddLoadNoteCard
            loadId={loadId}
            viewerRole="broker"
            viewerUserId={actorId}
            fallbackCompanyName={viewerIsAwardedBroker ? (detail?.awardedBroker?.company_name ?? null) : null}
            noteCount={notes.length}
            cardClassName="border-border bg-card shadow-sm"
          />
        </div>
      )}

      {/* LOAD NOTES (left) + DOCUMENTS (right) — two columns on desktop,
          stacked on mobile in that order. */}
      <div className="grid gap-3 mb-4 lg:grid-cols-2 items-start">
        <LoadNotesHistoryPanel
          loadId={loadId}
          viewerUserId={actorId}
          viewerRole="broker"
          cardClassName="border-border bg-card shadow-sm"
        />

        {dispatchId && (
          <div id="dispatch-documents">
            <DispatchDocumentsPanel
              dispatchId={dispatchId}
              loadId={loadId}
              uploaderId={actorId}
              counterpartyUserId={shipperUserId}
              shipperUserId={shipperUserId}
              canUpload={true}
              defaultDocType={isDelivered && !hasPod ? "pod" : "bol"}
              onChanged={refreshAll}
              cardClassName="border-border bg-card shadow-sm"
            />
          </div>
        )}
      </div>

      {/* FORMATTED DISPATCH SHEET — the same layout as the printed document.
          Replaces the previous Schedule / Stops / Load Details / Shipper cards. */}
      <div className="mb-4 rounded-lg border border-border/70 shadow-sm overflow-hidden bg-white">
        <DispatchSheet
          fill
          load={{
            load_number: load.load_number,
            origin_city: load.origin_city,
            origin_state: load.origin_state,
            destination_city: load.destination_city,
            destination_state: load.destination_state,
            equipment_type: load.equipment_type,
            pickup_date: load.pickup_date,
            delivery_date: load.delivery_date ?? null,
            commodity: loadCommodity || load.commodity || null,
            weight_lbs: loadWeight ?? (load.weight_lbs != null ? Number(load.weight_lbs) : null),
            length_ft: load.length_ft ?? null,
            total_miles: totalMiles,
            special_instructions: load.special_instructions ?? null,
            shipper_notes_to_brokers: load.shipper_notes_to_brokers ?? null,
            awarded_at: awardedAt,
          }}
          stops={stopsForDisplay.map((s) => ({
            id: (s as any).id,
            stop_number: s.stop_number,
            stop_type: s.stop_type,
            name: s.name ?? null,
            address: s.address ?? null,
            city: s.city ?? null,
            state: s.state ?? null,
            postal_code: s.postal_code ?? null,
            scheduled_date: s.scheduled_date ?? null,
            scheduled_time_start: s.scheduled_time_start ?? null,
            scheduled_time_end: s.scheduled_time_end ?? null,
            instructions: s.instructions ?? null,
            contact_name: s.contact_name ?? null,
            contact_phone: s.contact_phone ?? null,
            contact_email: s.contact_email ?? null,
            pickup_number: (s as any).pickup_number ?? null,
            reference_number: s.reference_number ?? null,
            pallet_count: s.pallet_count ?? null,
            confidential_notes:
              [s.confidential_notes, loadNotes && s.stop_number === 1 && s.stop_type === "pickup" ? loadNotes : null]
                .filter(Boolean).join("\n\n") || null,
          }))}
          broker={
            shipper
              ? null
              : null
          }
          awardedAmount={awardedAmount}
          totalMiles={totalMiles}
        />
      </div>

      {/* Shipper contact lives near the action bar; awarded amount now lives in
          the award banner; Load Documents moved above the Dispatch Sheet; the
          bottom event timeline was removed (top stage tracker is the source). */}


      {/* CREDIT APPLICATION — auto-populated for the winning broker.
          Was previously only reachable from the opportunity page; re-linked
          here so brokers who came in via "Open Dispatch" can still get to it. */}
      <div className="mt-4">
        <CreditAppFillPanel loadId={loadId} />
      </div>

      {/* CLOSE LOAD — explicit confirmation. Never marks POD Uploaded. */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this load?</AlertDialogTitle>
            <AlertDialogDescription>
              {hasPod
                ? "This marks the load Closed. Documents and notes are kept."
                : "No POD is on file. Closing does NOT mark POD Uploaded — the POD stays its own milestone and can still be uploaded later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doCloseLoad(); }}
              disabled={closing}
              className="bg-brand-teal hover:bg-brand-teal/90"
            >
              {closing ? "Closing…" : "Close Load"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* REOPEN LOAD — required before any milestone correction on a closed load. */}
      <AlertDialog open={confirmReopen} onOpenChange={setConfirmReopen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this load?</AlertDialogTitle>
            <AlertDialogDescription>
              Reopening clears the closed status so milestones can be corrected.
              Uploaded documents (BOL/POD), notes and all history are preserved —
              if a POD is on file, POD Uploaded stays filled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doReopenLoad(); }}
              disabled={closing}
              className="bg-brand-teal hover:bg-brand-teal/90"
            >
              {closing ? "Reopening…" : "Reopen Load"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>

  );
}

